/**
 * Pages alertes municipales — Phase 2A étendue L15b.
 *
 * Exporte trois composants :
 *   - AlertesListPage      : liste paginée filtrable + bouton nouvelle alerte
 *   - AlerteNouvellePage   : formulaire création avec matrice canaux interactive
 *   - AlerteDetailPage     : détail + workflow trigger/resolve/cancel + tableau envois
 *
 * Refonte R7.2 (mai 2026) :
 *   - Topbar glass cohérent avec halo grc
 *   - Sub-nav avec tabs pills glass (cohérent R7.1)
 *   - Niveaux d'alerte en mappings statiques Tailwind-purge-safe :
 *       info=success (vert), avis=warning (ambre), alerte=urgent (rouge), crise=ink (noir/dramatique)
 *   - Matrice canaux en grid bento glass avec CanalToggle/CanalStatus refondus
 *   - Workflow actions en Card glass séparée avec Alert prévention
 *   - Log envois avec progress bars success + détails en details/summary
 *
 * Logique métier 100% préservée :
 *   - api.grc.alertes.list/create/get/trigger/resolve/cancel/delete
 *   - 4 niveaux info/avis/alerte/crise avec defaultCanaux différents par niveau
 *   - canTrigger (super_admin/admin/manager), canDelete (super_admin/admin)
 *   - 8 catégories (météo, sécurité, sanitaire, voirie, réseau, événement, exercise, autre)
 *   - 5 canaux (site, mail, pushPwa, sms, panneau) avec warnings sms/panneau (MVP)
 *   - customCanaux : auto-update canaux quand niveau change sauf si checkbox custom cochée
 *   - confirmTrigger : 2-step confirmation pour déclenchement
 *   - Workflow : brouillon → active → résolue/annulée, suppression admin uniquement
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  Button, Badge, Card, Alert, Field, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { GrcLayout } from '@/components/grc/GrcLayout';
import {
  AlertOctagon, AlertTriangle, Bell, Info, BellPlus,
  ArrowLeft, Play, Check, X, Trash2, Mail, Smartphone, MessageSquare,
  Monitor, Globe, ChevronRight, AlertCircle, CheckCircle2, FileDown, FileSpreadsheet,
  Siren,
} from 'lucide-react';
import type {
  GrcAlerte, GrcAlerteDetail, GrcAlerteNiveau, GrcAlertePreset, GrcIncident,
} from '@roronoa-collectivites/shared-types';

// =============================================================================
// MAPPINGS NIVEAUX (Tailwind purge-safe)
// =============================================================================

// Mappings statiques explicites pour que Tailwind les détecte au purge
const NIVEAU_STYLES: Record<GrcAlerteNiveau, {
  iconBg: string;          // bg-success/15 etc.
  iconText: string;        // text-success etc.
  haloColor: string;       // oklch var pour halo radial
  badgeBg: string;         // bg-success text-white pour headline
  borderActive: string;    // border-success pour sélection card
  bgTint: string;          // bg-success/10 pour sélection card
}> = {
  info: {
    iconBg: 'bg-success/15',
    iconText: 'text-success',
    haloColor: 'oklch(var(--c-success) / 0.20)',
    badgeBg: 'bg-success text-white',
    borderActive: 'border-success',
    bgTint: 'bg-success/10',
  },
  avis: {
    iconBg: 'bg-warning/15',
    iconText: 'text-warning',
    haloColor: 'oklch(var(--c-warning) / 0.20)',
    badgeBg: 'bg-warning text-white',
    borderActive: 'border-warning',
    bgTint: 'bg-warning/10',
  },
  alerte: {
    iconBg: 'bg-urgent/15',
    iconText: 'text-urgent',
    haloColor: 'oklch(var(--c-urgent) / 0.20)',
    badgeBg: 'bg-urgent text-white',
    borderActive: 'border-urgent',
    bgTint: 'bg-urgent/10',
  },
  crise: {
    iconBg: 'bg-ink/15',
    iconText: 'text-ink',
    haloColor: 'oklch(var(--c-ink) / 0.20)',
    badgeBg: 'bg-ink text-bg',
    borderActive: 'border-ink',
    bgTint: 'bg-ink/10',
  },
};

const NIVEAU_INFO: Record<GrcAlerteNiveau, {
  label: string;
  desc: string;
  Icon: typeof Info;
  defaultCanaux: { site: boolean; mail: boolean; pushPwa: boolean; sms: boolean; panneau: boolean };
}> = {
  info: {
    label: 'Information',
    desc: 'Communication générale sans urgence',
    Icon: Info,
    defaultCanaux: { site: true, mail: true, pushPwa: false, sms: false, panneau: false },
  },
  avis: {
    label: 'Avis',
    desc: 'Avis aux administrés (travaux, événement)',
    Icon: Bell,
    defaultCanaux: { site: true, mail: true, pushPwa: true, sms: false, panneau: false },
  },
  alerte: {
    label: 'Alerte',
    desc: 'Alerte sécurité ou météo — SMS additionnel',
    Icon: AlertTriangle,
    defaultCanaux: { site: true, mail: true, pushPwa: true, sms: true, panneau: false },
  },
  crise: {
    label: 'Crise',
    desc: 'Crise majeure — tous canaux + activation PCS',
    Icon: AlertOctagon,
    defaultCanaux: { site: true, mail: true, pushPwa: true, sms: true, panneau: false },
  },
};

const CATEGORIES = [
  { value: 'meteo',     label: 'Météo' },
  { value: 'securite',  label: 'Sécurité' },
  { value: 'sanitaire', label: 'Sanitaire (eau, épidémie)' },
  { value: 'voirie',    label: 'Voirie / circulation' },
  { value: 'reseau',    label: 'Réseau (électricité, eau)' },
  { value: 'evenement', label: 'Événement municipal' },
  { value: 'exercise',  label: 'Exercice / test' },
  { value: 'autre',     label: 'Autre' },
];

const STATUT_BADGE: Record<string, { label: string; variant: 'neutral' | 'warning' | 'success' | 'urgent' }> = {
  brouillon: { label: 'Brouillon', variant: 'neutral' },
  active:    { label: 'Active',    variant: 'urgent' },
  resolue:   { label: 'Résolue',   variant: 'success' },
  annulee:   { label: 'Annulée',   variant: 'neutral' },
};

// Styles inputs/selects glass
const INPUT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';
const TEXTAREA_CLS = 'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

// =============================================================================
// LAYOUT GRC ALERTES — topbar glass + sub-nav
// =============================================================================

function GrcAlerteLayout({
  title, eyebrow = 'GRC & communication', icon = BellPlus, children, action,
}: {
  title: string;
  eyebrow?: string;
  icon?: typeof BellPlus;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <GrcLayout active="communication">
      {/* Sous-onglets de la section Communication */}
      <div className="border-b border-border px-4 py-2">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
          <GrcTab onClick={() => navigate('/grc/messages')}>Boîte citoyenne</GrcTab>
          <GrcTab onClick={() => navigate('/grc/messagerie')}>Messagerie interne</GrcTab>
          <GrcTab active>Alertes municipales</GrcTab>
          <GrcTab onClick={() => navigate('/grc/newsletters')}>Newsletter</GrcTab>
          {['super_admin', 'admin'].includes(user?.role ?? '') && (
            <GrcTab onClick={() => navigate('/grc/sms-config')}>Passerelle SMS</GrcTab>
          )}
        </div>
      </div>

      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5 animate-fade-in">
        {/* En-tête de page : LogoMark + titre */}
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={icon} accent="grc" size="lg" />
          <div className="min-w-0">
            <SectionEyebrow as="div">{eyebrow}</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              {title}
            </h1>
          </div>
          {action && <div className="ml-auto">{action}</div>}
        </div>

        {children}
      </main>
    </GrcLayout>
  );
}

// Tab pill (cohérent avec R7.1)
function GrcTab({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  if (active) {
    return (
      <span className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 text-xs font-medium text-grc-fg backdrop-blur-glass">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </button>
  );
}

// =============================================================================
// LISTE
// =============================================================================

export function AlertesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.grc.alertes.list>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatut, setFilterStatut] = useState<string>('all');
  const [filterNiveau, setFilterNiveau] = useState<string>('');

  const canCreate = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');

  async function reload() {
    setLoading(true); setError(null);
    try {
      const res = await api.grc.alertes.list({
        statut: filterStatut === 'all' ? undefined : filterStatut,
        niveau: filterNiveau || undefined,
      });
      setData(res);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [filterStatut, filterNiveau]);

  return (
    <GrcAlerteLayout
      title="Alertes municipales"
      icon={BellPlus}
      action={canCreate ? (
        <Button variant="primary" size="sm" onClick={() => navigate('/grc/alertes/nouvelle')}>
          <BellPlus className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Nouvelle alerte</span>
        </Button>
      ) : undefined}
    >
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Filtres en Card glass */}
      <Card variant="glass" padded className="mb-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1.5 flex-wrap">
            <FilterTab active={filterStatut === 'all'}       onClick={() => setFilterStatut('all')}>Toutes</FilterTab>
            <FilterTab active={filterStatut === 'active'}    onClick={() => setFilterStatut('active')} tone="urgent">
              Actives ({data?.activeCount ?? 0})
            </FilterTab>
            <FilterTab active={filterStatut === 'brouillon'} onClick={() => setFilterStatut('brouillon')}>Brouillons</FilterTab>
            <FilterTab active={filterStatut === 'resolue'}   onClick={() => setFilterStatut('resolue')}>Résolues</FilterTab>
            <FilterTab active={filterStatut === 'annulee'}   onClick={() => setFilterStatut('annulee')}>Annulées</FilterTab>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs">
            <label className="text-ink-muted uppercase tracking-r-eyebrow" htmlFor="niveau-filter">Niveau :</label>
            <select
              id="niveau-filter"
              value={filterNiveau}
              onChange={(e) => setFilterNiveau(e.target.value)}
              className="h-9 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-xs text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
            >
              <option value="">Tous</option>
              <option value="info">Information</option>
              <option value="avis">Avis</option>
              <option value="alerte">Alerte</option>
              <option value="crise">Crise</option>
            </select>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <div
              className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-grc border-t-transparent"
              role="status"
              aria-label="Chargement"
            />
            <p className="mt-4 text-sm text-ink-muted">Chargement des alertes…</p>
          </div>
        </Card>
      ) : !data || data.items.length === 0 ? (
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <div className="flex justify-center mb-3">
              <LogoMark icon={Bell} accent="grc" size="lg" />
            </div>
            <p className="text-sm text-ink-muted">Aucune alerte pour ce filtre</p>
            {canCreate && (
              <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/grc/alertes/nouvelle')}>
                <BellPlus className="h-4 w-4" aria-hidden="true" /> Créer la première alerte
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.items.map((a) => {
            const styles = NIVEAU_STYLES[a.niveau];
            const niveauInfo = NIVEAU_INFO[a.niveau];
            const statut = STATUT_BADGE[a.statut] ?? STATUT_BADGE.brouillon!;
            const isActive = a.statut === 'active';

            return (
              <Link
                key={a.id}
                to={`/grc/alertes/${a.id}`}
                className={[
                  'group block w-full rounded-r-xl border backdrop-blur-glass px-4 py-3 shadow-soft',
                  'transition-all duration-200 ease-out-expo',
                  'hover:-translate-y-0.5 hover:shadow-glass-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                  isActive ? 'border-urgent/40' : 'border-border hover:border-grc/30',
                ].join(' ')}
                style={{
                  backgroundImage: isActive
                    ? 'radial-gradient(160px 80px at 0% 50%, oklch(var(--c-urgent) / 0.12), transparent 70%), linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))'
                    : 'linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))',
                }}
              >
                <div className="flex items-center gap-3">
                  <div className={`inline-grid h-9 w-9 place-items-center rounded-r-lg ${styles.iconBg} flex-shrink-0`} aria-hidden="true">
                    <niveauInfo.Icon className={`h-4 w-4 ${styles.iconText}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className={`text-2xs font-bold uppercase tracking-r-eyebrow ${styles.iconText}`}>
                        {niveauInfo.label}
                      </span>
                      <Badge variant={statut.variant} size="sm">{statut.label}</Badge>
                      {isActive && (
                        <span className="text-2xs text-ink-muted">
                          depuis {a.declencheAt
                            ? new Date(a.declencheAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                            : '—'}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 truncate font-medium text-ink">{a.titre}</p>
                    <p className="truncate text-xs text-ink-muted">{a.message}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-dim transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </GrcAlerteLayout>
  );
}

function FilterTab({
  active, onClick, children, tone = 'neutral',
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  tone?: 'neutral' | 'urgent';
}) {
  // Mappings statiques
  const activeStyles = tone === 'urgent'
    ? 'bg-gradient-urgent text-white shadow-glow-urgent border-urgent/40'
    : 'bg-ink text-bg border-ink shadow-soft';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex h-8 items-center justify-center rounded-r-pill border px-3 text-xs font-medium transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        active
          ? activeStyles
          : 'border-border bg-surface/40 backdrop-blur-glass text-ink-muted hover:bg-surface/70 hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// =============================================================================
// CRÉATION
// =============================================================================

export function AlerteNouvellePage() {
  const navigate = useNavigate();
  const [niveau, setNiveau] = useState<GrcAlerteNiveau>('avis');
  const [categorie, setCategorie] = useState('voirie');
  const [titre, setTitre] = useState('');
  const [message, setMessage] = useState('');
  const [messageLong, setMessageLong] = useState('');
  const [zone, setZone] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [canaux, setCanaux] = useState(NIVEAU_INFO.avis.defaultCanaux);
  const [customCanaux, setCustomCanaux] = useState(false);
  const [secteurs, setSecteurs] = useState<Array<{ id: string; libelle: string }>>([]);
  const [secteurIds, setSecteurIds] = useState<string[]>([]);
  // Item 8.8 — modèles d'alerte : pré-remplissent le formulaire, ne créent rien.
  const [presets, setPresets] = useState<GrcAlertePreset[]>([]);
  const [presetApplique, setPresetApplique] = useState<string | null>(null);
  // Mig 234 — rattacher à un incident en cours plutôt que d'ouvrir un nouveau
  // dossier : c'est ce qui évite quatre titres pour un seul événement.
  const [incidents, setIncidents] = useState<GrcIncident[]>([]);
  const [incidentId, setIncidentId] = useState<string>('');

  // Secteurs actifs de la commune (pour le ciblage SMS optionnel).
  useEffect(() => {
    void (async () => {
      try { setSecteurs((await api.grc.sms.secteurs.list()).secteurs.map((s) => ({ id: s.id, libelle: s.libelle }))); }
      catch { /* segmentation optionnelle */ }
    })();
  }, []);

  // Incidents encore ouverts — ceux qu'on peut rejoindre.
  useEffect(() => {
    void (async () => {
      try { setIncidents((await api.grc.alertes.incidents.list('ouvert')).incidents); }
      catch { /* le rattachement est optionnel */ }
    })();
  }, []);

  // Modèles actifs de la commune. Absents = pas de bloc affiché : on ne montre
  // pas un sélecteur vide sur un écran déjà long.
  useEffect(() => {
    void (async () => {
      try { setPresets((await api.grc.alertes.presets.list()).presets); }
      catch { /* les modèles sont un confort, pas un prérequis */ }
    })();
  }, []);

  /**
   * Applique un modèle. On coche `customCanaux` parce que le modèle porte SES
   * canaux : sans ça, le useEffect de niveau les écraserait aussitôt par la
   * matrice par défaut, et une commune qui a volontairement coupé le SMS sur
   * son modèle « orage » le verrait revenir.
   */
  function appliquerPreset(preset: GrcAlertePreset) {
    setNiveau(preset.niveau);
    setCategorie(preset.categorie);
    if (preset.titre) setTitre(preset.titre);
    if (preset.message) setMessage(preset.message);
    if (preset.messageLong) setMessageLong(preset.messageLong);
    if (preset.zoneGeographique) setZone(preset.zoneGeographique);
    setSecteurIds(preset.secteurIds);
    setCustomCanaux(true);
    setCanaux(preset.canaux);
    setPresetApplique(preset.id);
  }

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-update canaux quand niveau change (sauf si custom)
  useEffect(() => {
    if (!customCanaux) {
      setCanaux(NIVEAU_INFO[niveau].defaultCanaux);
    }
  }, [niveau, customCanaux]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const created = await api.grc.alertes.create({
        niveau,
        categorie,
        titre: titre.trim(),
        message: message.trim(),
        messageLong: messageLong.trim() || undefined,
        zoneGeographique: zone.trim() || undefined,
        secteurIds: (customCanaux ? canaux.sms : NIVEAU_INFO[niveau].defaultCanaux.sms) && secteurIds.length > 0 ? secteurIds : undefined,
        dateDebut: dateDebut || undefined,
        dateFin: dateFin || undefined,
        canaux: customCanaux ? canaux : undefined,
        incidentId: incidentId || undefined,
      });
      navigate(`/grc/alertes/${created.id}`);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de création');
    } finally {
      setSubmitting(false);
    }
  }

  const niveauInfo = NIVEAU_INFO[niveau];

  return (
    <GrcAlerteLayout
      title="Nouvelle alerte municipale"
      icon={BellPlus}
      action={
        <Link
          to="/grc/alertes"
          className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-grc-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Retour
        </Link>
      }
    >
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 0. Modèles d'alerte — item 8.8 */}
        {presets.length > 0 && (
          <Card variant="glass" padded>
            <SectionEyebrow as="h2">Partir d'un modèle</SectionEyebrow>
            <p className="mt-2 mb-3 text-sm text-ink-muted">
              Le modèle remplit le formulaire — niveau, texte, canaux et secteurs
              destinataires. Rien n'est envoyé : relisez, corrigez, puis créez le
              brouillon.
            </p>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => {
                const actif = presetApplique === p.id;
                const styles = NIVEAU_STYLES[p.niveau];
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => appliquerPreset(p)}
                    aria-pressed={actif}
                    className={[
                      'inline-flex items-center gap-1.5 rounded-r-lg border px-3 py-1.5 text-sm transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                      actif
                        ? `${styles.borderActive} ${styles.bgTint} font-medium`
                        : 'border-border bg-surface/40 text-ink hover:border-grc/40 hover:bg-surface/60',
                    ].join(' ')}
                  >
                    <Bell className={`h-3.5 w-3.5 ${styles.iconText}`} aria-hidden="true" />
                    {p.libelle}
                    {p.canaux.sms && (
                      <Badge variant="warning" size="sm">SMS</Badge>
                    )}
                  </button>
                );
              })}
            </div>
            {presetApplique && (
              <Alert variant="info" icon={Info} className="mt-3">
                Modèle appliqué. Vérifiez surtout les canaux et les secteurs avant de créer.
              </Alert>
            )}
          </Card>
        )}

        {/* 0 bis. Rattachement à un incident — mig 234 */}
        {incidents.length > 0 && (
          <Card variant="glass" padded>
            <SectionEyebrow as="h2">Incident</SectionEyebrow>
            <p className="mt-2 mb-3 text-sm text-ink-muted">
              Si cette alerte concerne un événement déjà ouvert — une coupure qui
              s'aggrave, une route toujours barrée — rattachez-la. Le journal
              couvrira alors tout l'événement, pas cette seule étape.
            </p>
            <Field label="Rattacher à un incident en cours" htmlFor="alerte-incident">
              <select
                id="alerte-incident"
                value={incidentId}
                onChange={(e) => setIncidentId(e.target.value)}
                className={INPUT_CLS}
              >
                <option value="">Nouvel incident (le plus fréquent)</option>
                {incidents.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.reference} — {i.libelle} ({i.nbAlertes} étape{i.nbAlertes > 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            </Field>
          </Card>
        )}

        {/* 1. Sélecteur niveau visuel */}
        <Card variant="glass" padded>
          <SectionEyebrow as="h2">1. Niveau de l'alerte</SectionEyebrow>
          <p className="mt-2 mb-3 text-sm text-ink-muted">
            Sélectionnez le niveau approprié. Les canaux par défaut sont adaptés
            automatiquement selon la matrice municipale.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {(['info', 'avis', 'alerte', 'crise'] as const).map((n) => {
              const i = NIVEAU_INFO[n];
              const styles = NIVEAU_STYLES[n];
              const selected = niveau === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNiveau(n)}
                  aria-pressed={selected}
                  className={[
                    'flex flex-col items-start gap-1 rounded-r-xl border-2 backdrop-blur-glass p-3 text-left transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                    selected
                      ? `${styles.borderActive} ${styles.bgTint} shadow-soft`
                      : 'border-border bg-surface/40 hover:border-grc/40 hover:bg-surface/60',
                  ].join(' ')}
                  style={
                    selected
                      ? { backgroundImage: `radial-gradient(120px 60px at 50% 0%, ${styles.haloColor}, transparent 70%)` }
                      : undefined
                  }
                >
                  <i.Icon className={`h-5 w-5 ${styles.iconText}`} aria-hidden="true" />
                  <span className={`text-sm font-semibold ${selected ? styles.iconText : 'text-ink'}`}>
                    {i.label}
                  </span>
                  <span className="text-2xs text-ink-muted">{i.desc}</span>
                </button>
              );
            })}
          </div>
        </Card>

        {/* 2. Contenu */}
        <Card variant="glass" padded>
          <SectionEyebrow as="h2">2. Contenu</SectionEyebrow>

          <div className="mt-3 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Catégorie *" htmlFor="alerte-categorie">
                <select
                  id="alerte-categorie"
                  value={categorie}
                  onChange={(e) => setCategorie(e.target.value)}
                  required
                  className={INPUT_CLS}
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Zone géographique" htmlFor="alerte-zone">
                <input
                  id="alerte-zone"
                  value={zone}
                  onChange={(e) => setZone(e.target.value)}
                  maxLength={300}
                  placeholder="Ex : Quartier de l'église, toute la commune"
                  className={INPUT_CLS}
                />
              </Field>
            </div>

            {secteurs.length > 0 && (
              <Field label="Cibler des secteurs (SMS)" htmlFor="alerte-secteurs">
                <div className="flex flex-wrap gap-1.5" id="alerte-secteurs">
                  {secteurs.map((s) => {
                    const on = secteurIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSecteurIds((cur) => on ? cur.filter((x) => x !== s.id) : [...cur, s.id])}
                        className={`rounded-full border px-3 py-1 text-xs transition ${on ? 'border-accent bg-accent/10 text-accent-fg font-medium' : 'border-border text-ink-muted hover:border-accent/50'}`}
                        aria-pressed={on}
                      >
                        {s.libelle}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-2xs text-ink-subtle">
                  {secteurIds.length === 0
                    ? 'Aucun secteur sélectionné : le SMS partira à tous les abonnés de la commune.'
                    : `Le SMS ne partira qu'aux abonnés des secteurs choisis (${secteurIds.length}).`}
                </p>
              </Field>
            )}

            <Field label="Titre *" htmlFor="alerte-titre">
              <input
                id="alerte-titre"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                required minLength={3} maxLength={200}
                placeholder="Ex : Coupure route D107 — pont en travaux"
                className={INPUT_CLS}
              />
            </Field>

            <Field
              label="Message court * (utilisé pour SMS / push, 500 caractères max)"
              htmlFor="alerte-message"
              hint={`${message.length} / 500`}
            >
              <textarea
                id="alerte-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required minLength={3} maxLength={500}
                rows={2}
                placeholder="Ex : La D107 sera fermée du 18/05 au 22/05 entre 8h et 18h. Itinéraire de déviation par la D104."
                className={TEXTAREA_CLS}
              />
            </Field>

            <Field label="Message long (optionnel — utilisé pour mail et site)" htmlFor="alerte-messageLong">
              <textarea
                id="alerte-messageLong"
                value={messageLong}
                onChange={(e) => setMessageLong(e.target.value)}
                maxLength={20000}
                rows={4}
                placeholder="Précisions, contacts utiles, itinéraires de déviation détaillés…"
                className={TEXTAREA_CLS}
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Début de l'événement" htmlFor="alerte-dateDebut">
                <input
                  id="alerte-dateDebut"
                  type="datetime-local"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Fin de l'événement" htmlFor="alerte-dateFin">
                <input
                  id="alerte-dateFin"
                  type="datetime-local"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                  className={INPUT_CLS}
                />
              </Field>
            </div>
          </div>
        </Card>

        {/* 3. Matrice canaux */}
        <Card variant="glass" padded>
          <div className="flex items-center justify-between gap-2">
            <SectionEyebrow as="h2">3. Canaux de diffusion</SectionEyebrow>
            <label className="flex items-center gap-1.5 text-xs text-ink-muted cursor-pointer">
              <input
                type="checkbox"
                checked={customCanaux}
                onChange={(e) => setCustomCanaux(e.target.checked)}
                className="h-3.5 w-3.5 accent-grc"
              />
              Personnaliser
            </label>
          </div>
          <p className="mt-2 mb-3 text-sm text-ink-muted">
            {customCanaux
              ? 'Cochez/décochez librement les canaux pour cette alerte.'
              : `Canaux par défaut pour le niveau « ${niveauInfo.label} ». Cochez "Personnaliser" pour modifier.`}
          </p>

          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            <CanalToggle
              Icon={Globe} label="Site web"
              checked={canaux.site} disabled={!customCanaux}
              onChange={(v) => setCanaux({ ...canaux, site: v })}
            />
            <CanalToggle
              Icon={Mail} label="Email"
              checked={canaux.mail} disabled={!customCanaux}
              onChange={(v) => setCanaux({ ...canaux, mail: v })}
              helper="aux abonnés newsletter"
            />
            <CanalToggle
              Icon={Smartphone} label="Push PWA"
              checked={canaux.pushPwa} disabled={!customCanaux}
              onChange={(v) => setCanaux({ ...canaux, pushPwa: v })}
              helper="notification navigateur"
            />
            <CanalToggle
              Icon={MessageSquare} label="SMS"
              checked={canaux.sms} disabled={!customCanaux}
              onChange={(v) => setCanaux({ ...canaux, sms: v })}
              helper="(payant · double validation à l'envoi)"
              warning
            />
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate('/grc/alertes')}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || !titre.trim() || !message.trim()}
          >
            {submitting ? 'Enregistrement…' : 'Créer le brouillon'}
          </Button>
        </div>

        <Alert variant="info" icon={Info}>
          <strong>Brouillon d'abord.</strong> Vous pourrez relire et déclencher l'envoi
          depuis la page de détail. Les notifications ne partent <em>pas</em> à la création.
        </Alert>
      </form>
    </GrcAlerteLayout>
  );
}

function CanalToggle({
  Icon, label, checked, disabled, onChange, helper, warning,
}: {
  Icon: typeof Mail;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  helper?: string;
  warning?: boolean;
}) {
  return (
    <label
      className={[
        'flex cursor-pointer flex-col gap-1 rounded-r-lg border-2 backdrop-blur-glass p-2.5 transition-all',
        checked
          ? warning
            ? 'border-warning bg-warning/10'
            : 'border-grc bg-grc/10'
          : 'border-border bg-surface/40',
        disabled ? 'cursor-not-allowed opacity-70' : 'hover:border-grc/40',
      ].join(' ')}
    >
      <div className="flex items-center gap-1.5">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="h-3.5 w-3.5 accent-grc"
        />
        <Icon
          className={`h-3.5 w-3.5 ${checked ? (warning ? 'text-warning' : 'text-grc-fg') : 'text-ink-muted'}`}
          aria-hidden="true"
        />
        <span className="text-xs font-medium text-ink">{label}</span>
      </div>
      {helper && <span className="text-2xs text-ink-muted">{helper}</span>}
    </label>
  );
}

// =============================================================================
// DÉTAIL
// =============================================================================

export function AlerteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alerte, setAlerte] = useState<GrcAlerteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmTrigger, setConfirmTrigger] = useState(false);
  // Double validation SMS (canal payant) : estimation de coût + saisie « ENVOYER ».
  const [smsEstim, setSmsEstim] = useState<Awaited<ReturnType<typeof api.grc.sms.estimation>> | null>(null);
  const [smsEstimLoading, setSmsEstimLoading] = useState(false);
  const [motConfirmation, setMotConfirmation] = useState('');

  const canTrigger = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');
  const canDelete  = ['super_admin', 'admin'].includes(user?.role ?? '');

  // Item 8.9 — pièce jointe. Modifiable tant que l'alerte est un brouillon :
  // une fois déclenchée, les mails sont partis, la page publique doit rester
  // fidèle à ce que les destinataires ont reçu.
  const [pjLoading, setPjLoading] = useState(false);
  const [pjError, setPjError] = useState<string | null>(null);

  // Mig 234 — clôture manuelle de l'incident. La clôture automatique se
  // déclenche quand la dernière alerte est résolue ; ce bouton couvre le cas où
  // c'est un agent qui constate la fin de l'événement.
  const [incident, setIncident] = useState<GrcIncident | null>(null);
  const [clotureLoading, setClotureLoading] = useState(false);

  useEffect(() => {
    if (!alerte?.incidentId) { setIncident(null); return; }
    void (async () => {
      try { setIncident(await api.grc.alertes.incidents.get(alerte.incidentId!)); }
      catch { setIncident(null); }
    })();
  }, [alerte?.incidentId, alerte?.statut]);

  async function cloturerIncident() {
    if (!incident) return;
    setClotureLoading(true); setError(null);
    try {
      setIncident(await api.grc.alertes.incidents.cloturer(incident.id));
      setSuccess('Incident clôturé');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Clôture impossible');
    } finally { setClotureLoading(false); }
  }

  async function handlePieceJointe(fichier: File) {
    if (!alerte) return;
    setPjLoading(true); setPjError(null);
    try {
      setAlerte({ ...alerte, ...(await api.grc.alertes.pieceJointe.upload(alerte.id, fichier)) });
      setSuccess('Document joint');
    } catch (err) {
      setPjError(err instanceof ApiException ? err.payload.error : 'Envoi impossible');
    } finally { setPjLoading(false); }
  }

  async function retirerPieceJointe() {
    if (!alerte) return;
    setPjLoading(true); setPjError(null);
    try {
      setAlerte({ ...alerte, ...(await api.grc.alertes.pieceJointe.supprimer(alerte.id)) });
    } catch (err) {
      setPjError(err instanceof ApiException ? err.payload.error : 'Suppression impossible');
    } finally { setPjLoading(false); }
  }

  async function ouvrirPieceJointe() {
    if (!alerte) return;
    setPjError(null);
    try {
      const { url } = await api.grc.alertes.pieceJointe.url(alerte.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setPjError(err instanceof ApiException ? err.payload.error : 'Document indisponible');
    }
  }

  // Charge l'estimation de coût SMS dès qu'on entre en confirmation, si le canal
  // SMS est activé sur l'alerte. Sert l'encart tarif de la double validation.
  useEffect(() => {
    if (confirmTrigger && alerte?.canaux.sms && !smsEstim && !smsEstimLoading) {
      setSmsEstimLoading(true);
      api.grc.sms.estimation(alerte.message)
        .then(setSmsEstim)
        .catch(() => setSmsEstim(null))
        .finally(() => setSmsEstimLoading(false));
    }
    if (!confirmTrigger) { setSmsEstim(null); setMotConfirmation(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmTrigger, alerte?.canaux.sms]);

  async function reload() {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const a = await api.grc.alertes.get(id);
      setAlerte(a);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [id]);

  const [exportingJournal, setExportingJournal] = useState<'pdf' | 'excel' | null>(null);
  async function doExportJournal(kind: 'pdf' | 'excel') {
    if (!alerte) return;
    setExportingJournal(kind); setError(null);
    try {
      if (kind === 'pdf') await api.grc.alertes.journalPdf(alerte.id);
      else await api.grc.alertes.journalExcel(alerte.id);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Export impossible');
    } finally { setExportingJournal(null); }
  }

  async function doAction(fn: () => Promise<GrcAlerte>, label: string) {
    setActionLoading(true); setError(null); setSuccess(null);
    try {
      await fn();
      setSuccess(label);
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur action');
    } finally {
      setActionLoading(false);
    }
  }

  async function doDelete() {
    if (!alerte) return;
    if (!confirm(`Supprimer définitivement l'alerte « ${alerte.titre} » ? Cette action est irréversible et efface aussi le log des envois.`)) return;
    try {
      await api.grc.alertes.delete(alerte.id);
      navigate('/grc/alertes');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur suppression');
    }
  }

  if (loading) {
    return (
      <GrcAlerteLayout title="Alerte municipale" icon={BellPlus}>
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <div
              className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-grc border-t-transparent"
              role="status"
              aria-label="Chargement"
            />
            <p className="mt-4 text-sm text-ink-muted">Chargement de l'alerte…</p>
          </div>
        </Card>
      </GrcAlerteLayout>
    );
  }

  if (error || !alerte) {
    return (
      <GrcAlerteLayout title="Alerte municipale" icon={BellPlus}>
        <div className="space-y-3">
          <Alert variant="error" icon={AlertCircle}>{error ?? 'Alerte introuvable'}</Alert>
          <Button variant="secondary" size="sm" onClick={() => navigate('/grc/alertes')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à la liste
          </Button>
        </div>
      </GrcAlerteLayout>
    );
  }

  const niveauInfo = NIVEAU_INFO[alerte.niveau];
  const styles = NIVEAU_STYLES[alerte.niveau];
  const statut = STATUT_BADGE[alerte.statut] ?? STATUT_BADGE.brouillon!;

  return (
    <GrcAlerteLayout
      title={`Alerte ${niveauInfo.label.toLowerCase()}`}
      icon={niveauInfo.Icon}
      action={
        <Link
          to="/grc/alertes"
          className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-grc-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Retour
        </Link>
      }
    >
      {success && (
        <Alert variant="success" icon={CheckCircle2} className="mb-3">
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">
          {error}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        {/* Colonne principale */}
        <div className="space-y-3">
          {/* Headline */}
          <Card variant="glass" padded>
            <div className="mb-3 flex flex-wrap items-baseline gap-2">
              <div className={`inline-flex items-center gap-1.5 rounded-r-pill ${styles.badgeBg} px-2.5 py-1 text-xs font-bold uppercase tracking-r-eyebrow`}>
                <niveauInfo.Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {niveauInfo.label}
              </div>
              <Badge variant={statut.variant} size="sm">{statut.label}</Badge>
              <span className="ml-auto text-2xs text-ink-muted">
                Créée {new Date(alerte.createdAt).toLocaleString('fr-FR')}
                {alerte.creeParFullName && <> par {alerte.creeParFullName}</>}
              </span>
            </div>

            <h2 className="font-display text-xl font-semibold tracking-r-snug text-ink">{alerte.titre}</h2>
            <p className="mt-2 text-sm text-ink">{alerte.message}</p>

            {alerte.messageLong && (
              <div className="mt-4 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{alerte.messageLong}</p>
              </div>
            )}

            <dl className="mt-4 grid gap-1 text-xs sm:grid-cols-2">
              {alerte.zoneGeographique && (
                <div>
                  <dt className="inline font-medium text-ink-muted">Zone :</dt>
                  <dd className="inline text-ink"> {alerte.zoneGeographique}</dd>
                </div>
              )}
              <div>
                <dt className="inline font-medium text-ink-muted">Catégorie :</dt>
                <dd className="inline text-ink"> {alerte.categorie}</dd>
              </div>
              {alerte.dateDebut && (
                <div>
                  <dt className="inline font-medium text-ink-muted">Début :</dt>
                  <dd className="inline text-ink"> {new Date(alerte.dateDebut).toLocaleString('fr-FR')}</dd>
                </div>
              )}
              {alerte.dateFin && (
                <div>
                  <dt className="inline font-medium text-ink-muted">Fin :</dt>
                  <dd className="inline text-ink"> {new Date(alerte.dateFin).toLocaleString('fr-FR')}</dd>
                </div>
              )}
              {alerte.declencheAt && (
                <div>
                  <dt className="inline font-medium text-ink-muted">Déclenchée :</dt>
                  <dd className="inline text-ink"> {new Date(alerte.declencheAt).toLocaleString('fr-FR')}</dd>
                </div>
              )}
              {alerte.resolueAt && (
                <div>
                  <dt className="inline font-medium text-ink-muted">Résolue :</dt>
                  <dd className="inline text-ink"> {new Date(alerte.resolueAt).toLocaleString('fr-FR')}</dd>
                  {alerte.resolueParFullName && <span className="text-ink-muted"> par {alerte.resolueParFullName}</span>}
                </div>
              )}
            </dl>
          </Card>

          {/* Matrice canaux (statut) */}
          <Card variant="glass" padded>
            <SectionEyebrow as="h2" className="mb-3">Canaux de diffusion</SectionEyebrow>
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              <CanalStatus Icon={Globe} label="Site web" active={alerte.canaux.site} />
              <CanalStatus
                Icon={Mail} label="Email"
                active={alerte.canaux.mail}
                count={alerte.statut !== 'brouillon' ? alerte.cibleMailCount : null}
              />
              <CanalStatus
                Icon={Smartphone} label="Push PWA"
                active={alerte.canaux.pushPwa}
                count={alerte.statut !== 'brouillon' ? alerte.ciblePushCount : null}
              />
              <CanalStatus Icon={MessageSquare} label="SMS" active={alerte.canaux.sms} />
              {alerte.canaux.panneau && (
                <CanalStatus Icon={Monitor} label="Panneau lumineux" active={alerte.canaux.panneau} />
              )}
            </div>
          </Card>

          {/* Log envois */}
          {alerte.statut !== 'brouillon' && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="mb-3">Log des envois</SectionEyebrow>
              {alerte.envoisSummary.length === 0 ? (
                <p className="text-xs text-ink-muted">Aucun envoi enregistré</p>
              ) : (
                <div className="space-y-2">
                  {alerte.envoisSummary.map((s) => {
                    const total = s.pending + s.sent + s.failed + s.skipped;
                    const pctSent = total > 0 ? Math.round((s.sent / total) * 100) : 0;
                    return (
                      <div
                        key={s.canal}
                        className="rounded-r-md border border-border bg-surface/40 backdrop-blur-glass p-3"
                      >
                        <div className="mb-1.5 flex items-baseline justify-between">
                          <span className="text-xs font-medium text-ink capitalize">
                            {s.canal.replace('_', ' ')}
                          </span>
                          <span className="text-2xs text-ink-muted">{total} destinataires</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-bg/60">
                          <div
                            className="h-full bg-success transition-[width]"
                            style={{ width: `${pctSent}%` }}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-3 text-2xs text-ink-muted">
                          {s.sent > 0    && <span><span className="font-medium text-success">{s.sent}</span> envoyés</span>}
                          {s.pending > 0 && <span><span className="font-medium text-warning">{s.pending}</span> en attente</span>}
                          {s.failed > 0  && <span><span className="font-medium text-urgent">{s.failed}</span> échecs</span>}
                          {s.skipped > 0 && <span><span className="font-medium text-ink">{s.skipped}</span> skipped</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {alerte.envoisRecents.length > 0 && (
                <details className="mt-4 rounded-r-md border border-border bg-surface/30 backdrop-blur-glass">
                  <summary className="cursor-pointer px-3 py-2 text-xs text-ink-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md">
                    Voir les {alerte.envoisRecents.length} derniers envois détaillés
                  </summary>
                  <div className="overflow-x-auto px-3 pb-3">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border text-left">
                          <th className="px-2 py-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Canal</th>
                          <th className="px-2 py-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Destinataire</th>
                          <th className="px-2 py-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Statut</th>
                          <th className="px-2 py-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Quand</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerte.envoisRecents.map((e) => (
                          <tr key={e.id} className="border-b border-border last:border-b-0">
                            <td className="px-2 py-1.5 capitalize text-ink">{e.canal.replace('_', ' ')}</td>
                            <td className="px-2 py-1.5 text-ink-muted truncate max-w-[200px]">
                              {e.destinataireAdresse ?? '—'}
                            </td>
                            <td className="px-2 py-1.5">
                              <Badge
                                variant={e.statut === 'sent' ? 'success' : e.statut === 'failed' ? 'urgent' : 'neutral'}
                                size="sm"
                              >
                                {e.statut}
                              </Badge>
                              {e.errorMessage && (
                                <p className="mt-0.5 text-2xs text-urgent">{e.errorMessage}</p>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-2xs text-ink-muted tabular-nums">
                              {e.sentAt ? new Date(e.sentAt).toLocaleTimeString('fr-FR') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </Card>
          )}
          {/* Pièce jointe — item 8.9 */}
          <Card variant="glass" padded>
            <SectionEyebrow as="h2" className="mb-3">Document joint</SectionEyebrow>

            {pjError && (
              <Alert variant="error" icon={AlertCircle} className="mb-3">{pjError}</Alert>
            )}

            {alerte.pieceJointe ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-r-lg border border-border bg-surface/40 p-3">
                <div className="flex min-w-0 items-center gap-2">
                  <FileDown className="h-4 w-4 shrink-0 text-grc-fg" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{alerte.pieceJointe.nom}</p>
                    <p className="text-2xs text-ink-muted tabular-nums">
                      {Math.max(1, Math.round(alerte.pieceJointe.tailleBytes / 1024))} Ko
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => void ouvrirPieceJointe()}>
                    Ouvrir
                  </Button>
                  {alerte.statut === 'brouillon' && canTrigger && (
                    <Button
                      type="button" variant="ghost" size="sm"
                      disabled={pjLoading}
                      onClick={() => void retirerPieceJointe()}
                    >
                      Retirer
                    </Button>
                  )}
                </div>
              </div>
            ) : alerte.statut === 'brouillon' && canTrigger ? (
              <>
                <label
                  htmlFor="alerte-pj"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-r-lg border border-border bg-surface/40 px-3 py-2 text-sm text-ink transition-all hover:border-grc/40 hover:bg-surface/60 focus-within:ring-2 focus-within:ring-grc"
                >
                  <FileDown className="h-4 w-4" aria-hidden="true" />
                  {pjLoading ? 'Envoi…' : 'Joindre un document'}
                </label>
                <input
                  id="alerte-pj"
                  type="file"
                  className="sr-only"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  disabled={pjLoading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handlePieceJointe(f);
                    e.target.value = '';
                  }}
                />
                <p className="mt-2 text-2xs text-ink-muted">
                  PDF ou image, 10 Mo maximum. Plan de rues, arrêté signé, affiche.
                </p>
              </>
            ) : (
              <p className="text-sm text-ink-muted">Aucun document joint.</p>
            )}

            {/* Le dire ici évite qu'un agent croie avoir diffusé un plan par SMS. */}
            <Alert variant="info" icon={Info} className="mt-3">
              Un document ne peut partir que sur le <strong>site</strong> et par
              <strong> mail</strong>. SMS, notification push et panneau lumineux ne
              savent pas porter de fichier.
            </Alert>
          </Card>
        </div>

        {/* Sidebar actions */}
        <aside className="space-y-3">
          {alerte.statut === 'brouillon' && canTrigger && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="mb-3">Déclencher l'envoi</SectionEyebrow>
              <Alert variant="warning" icon={AlertTriangle} className="mb-3">
                Le déclenchement enverra immédiatement les notifications sur tous les canaux activés.
                Cette action ne peut pas être annulée une fois lancée.
              </Alert>

              {!confirmTrigger ? (
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={() => setConfirmTrigger(true)}
                >
                  <Play className="h-4 w-4" aria-hidden="true" /> Déclencher
                </Button>
              ) : (
                <>
                  {/* Encart tarif SMS — n'apparaît que si le canal SMS est activé */}
                  {alerte.canaux.sms && (
                    <div className="mb-3 rounded-lg border border-urgent/30 bg-urgent/5 p-3">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-urgent">
                        <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                        Envoi SMS — canal payant
                      </p>
                      {smsEstimLoading ? (
                        <p className="mt-1 text-xs text-ink-muted">Estimation du coût…</p>
                      ) : smsEstim ? (
                        <div className="mt-1.5 space-y-1 text-xs text-ink-muted">
                          <p>
                            <span className="font-medium text-ink">{smsEstim.nbAbonnes}</span> abonné·e·s SMS
                            {smsEstim.nbSegments > 1 && <> · {smsEstim.nbSegments} SMS/message</>}
                            {' → '}
                            <span className="font-medium text-ink">{smsEstim.nbSmsTotal}</span> SMS
                          </p>
                          <p>
                            Coût estimé :{' '}
                            <span className="font-semibold text-ink">
                              {smsEstim.coutEstimeEur.toFixed(2).replace('.', ',')} €
                            </span>{' '}
                            ({smsEstim.prixEffectifEur.toFixed(3).replace('.', ',')} €/SMS
                            {smsEstim.modeCompte === 'passerelle'
                              ? ` — dont ${smsEstim.surcoutPasserelleEur.toFixed(2).replace('.', ',')} € gestion VPC)`
                              : ' — prix coûtant, sans marge)'}
                          </p>
                          {smsEstim.modeCompte === 'passerelle' && (
                            <p className={smsEstim.soldeSuffisant ? '' : 'font-semibold text-urgent'}>
                              Solde : {smsEstim.soldeSms} crédits
                              {!smsEstim.soldeSuffisant && ' — insuffisant, envoi SMS impossible'}
                            </p>
                          )}
                          {!smsEstim.canalPret && (
                            <p className="font-semibold text-urgent">
                              Passerelle SMS non configurée — les autres canaux partiront, pas le SMS.
                            </p>
                          )}
                          {smsEstim.nbAbonnes === 0 && (
                            <p>Aucun abonné SMS pour l’instant — le SMS ne partira à personne.</p>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1 text-xs text-ink-muted">
                          Coût non estimable (passerelle non configurée).
                        </p>
                      )}
                    </div>
                  )}

                  <p className="text-xs font-medium text-urgent">
                    {alerte.canaux.sms
                      ? 'Confirmer l’envoi ? Tapez ENVOYER pour débloquer.'
                      : 'Confirmer le déclenchement ?'}
                  </p>

                  {/* Saisie de confirmation obligatoire quand le SMS est activé */}
                  {alerte.canaux.sms && (
                    <input
                      type="text"
                      value={motConfirmation}
                      onChange={(e) => setMotConfirmation(e.target.value.toUpperCase())}
                      placeholder="ENVOYER"
                      aria-label="Tapez ENVOYER pour confirmer l’envoi SMS"
                      className="mt-2 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm tracking-widest focus:border-urgent focus:outline-none focus:ring-2 focus:ring-urgent/30"
                    />
                  )}

                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConfirmTrigger(false)}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="flex-1"
                      disabled={actionLoading || (alerte.canaux.sms && motConfirmation.trim() !== 'ENVOYER')}
                      onClick={() => doAction(() => api.grc.alertes.trigger(alerte.id), 'Alerte déclenchée — envois en cours')}
                    >
                      {alerte.canaux.sms ? 'Envoyer' : 'Oui, déclencher'}
                    </Button>
                  </div>
                </>
              )}

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 w-full"
                disabled={actionLoading}
                onClick={() => doAction(() => api.grc.alertes.cancel(alerte.id), 'Brouillon annulé')}
              >
                <X className="h-4 w-4" aria-hidden="true" /> Annuler ce brouillon
              </Button>
            </Card>
          )}

          {alerte.statut === 'active' && canTrigger && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="mb-3">Workflow</SectionEyebrow>
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="success"
                  className="w-full"
                  disabled={actionLoading}
                  onClick={() => doAction(() => api.grc.alertes.resolve(alerte.id), 'Alerte marquée comme résolue')}
                >
                  <Check className="h-4 w-4" aria-hidden="true" /> Marquer résolue
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  className="w-full"
                  disabled={actionLoading}
                  onClick={() => doAction(() => api.grc.alertes.cancel(alerte.id), 'Alerte annulée (fausse alerte)')}
                >
                  <X className="h-4 w-4" aria-hidden="true" /> Annuler (fausse alerte)
                </Button>
              </div>
            </Card>
          )}

          {(alerte.statut === 'resolue' || alerte.statut === 'annulee') && (
            <Card variant="glass" padded>
              <p className="text-xs text-ink-muted">
                <strong>Alerte clôturée.</strong> Les actions de modification sont désactivées.
              </p>
            </Card>
          )}

          {incident && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="mb-3">Incident</SectionEyebrow>
              <p className="text-sm font-medium text-ink">{incident.reference}</p>
              <p className="text-xs text-ink-muted">{incident.libelle}</p>
              <p className="mt-2 text-2xs text-ink-muted">
                {incident.nbAlertes} étape{incident.nbAlertes > 1 ? 's' : ''}
                {incident.nbAlertesActives > 0
                  ? ` · ${incident.nbAlertesActives} en cours`
                  : ''}
              </p>

              {incident.statut === 'cloture' ? (
                <Alert variant="success" icon={CheckCircle2} className="mt-3">
                  Clôturé{incident.clotureMode === 'manuel'
                    ? ` par ${incident.clotureParFullName ?? 'un agent'}`
                    : ' automatiquement (dernière alerte résolue)'}.
                </Alert>
              ) : canTrigger && (
                <>
                  <Button
                    type="button" variant="secondary" size="sm"
                    className="mt-3 w-full"
                    disabled={clotureLoading || incident.nbAlertesActives > 0}
                    onClick={() => void cloturerIncident()}
                  >
                    <Check className="h-4 w-4" aria-hidden="true" />
                    {clotureLoading ? 'Clôture…' : 'Clôturer l\'incident'}
                  </Button>
                  {incident.nbAlertesActives > 0 && (
                    <p className="mt-2 text-2xs text-ink-muted">
                      Une alerte est encore active : elle s'affiche sur le site
                      public. Résolvez-la d'abord.
                    </p>
                  )}
                </>
              )}
            </Card>
          )}

          {/* Option A (24/07) — le journal n'est plus réservé aux niveaux hauts :
              le niveau est choisi dans l'urgence, un « avis » peut dégénérer,
              et l'export fonctionnait déjà pour tous les niveaux. */}
          <Card variant="glass" padded>
            <SectionEyebrow as="h2" className="mb-3">Journal de l'incident</SectionEyebrow>
            <p className="mb-3 text-xs text-ink-muted">
              Suivi horodaté complet — déroulé, envois, communications rattachées
              et documents joints. Preuve de l'action municipale.
            </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button" variant="secondary" size="sm"
                  onClick={() => void doExportJournal('pdf')}
                  disabled={exportingJournal !== null}
                >
                  <FileDown className="h-4 w-4" aria-hidden="true" />
                  {exportingJournal === 'pdf' ? 'Export…' : 'PDF'}
                </Button>
                <Button
                  type="button" variant="secondary" size="sm"
                  onClick={() => void doExportJournal('excel')}
                  disabled={exportingJournal !== null}
                >
                  <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
                  {exportingJournal === 'excel' ? 'Export…' : 'Excel'}
                </Button>
                {/* Vague A2 — l'annuaire d'urgence (PCS, mig 253) est à un clic
                    depuis la fiche d'incident. Chercher un numéro de préfecture
                    en pleine crise en naviguant dans les menus, c'est le genre
                    de détail qui coûte des minutes au mauvais moment. */}
                <Button
                  type="button" variant="secondary" size="sm"
                  onClick={() => navigate('/mon-espace')}
                >
                  <Siren className="h-4 w-4" aria-hidden="true" />
                  Numéros d'urgence
                </Button>
              </div>

              {/* L'incident, quand il existe : c'est le périmètre réel de l'export. */}
              {alerte.incidentReference && (
                <p className="mt-3 border-t border-border pt-3 text-2xs text-ink-muted">
                  Incident <strong className="text-ink">{alerte.incidentReference}</strong>
                  {alerte.incidentLibelle ? ` — ${alerte.incidentLibelle}` : ''}
                </p>
              )}
            </Card>

          {canDelete && (
            <Card variant="glass" padded>
              <Button
                type="button"
                variant="danger"
                size="sm"
                className="w-full"
                onClick={() => void doDelete()}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer définitivement
              </Button>
              <p className="mt-2 text-2xs text-ink-muted">
                Action réservée aux administrateurs. Efface l'historique d'envois.
              </p>
            </Card>
          )}

          {alerte.statut === 'brouillon' && (
            <Card variant="glass" padded>
              <p className="text-xs text-ink-muted">
                <strong className="text-ink">Aperçu :</strong> ce brouillon n'a pas encore été envoyé.
                Relisez attentivement le contenu avant de déclencher.
              </p>
            </Card>
          )}
        </aside>
      </div>
    </GrcAlerteLayout>
  );
}

function CanalStatus({
  Icon, label, active, count,
}: {
  Icon: typeof Mail;
  label: string;
  active: boolean;
  count?: number | null;
}) {
  return (
    <div
      className={[
        'rounded-r-lg border backdrop-blur-glass p-2.5 transition-colors',
        active
          ? 'border-grc/40 bg-grc/5'
          : 'border-border bg-surface/30 opacity-60',
      ].join(' ')}
    >
      <div className="flex items-center gap-1.5">
        <Icon
          className={`h-3.5 w-3.5 ${active ? 'text-grc-fg' : 'text-ink-dim'}`}
          aria-hidden="true"
        />
        <span className="text-xs font-medium text-ink">{label}</span>
      </div>
      {active && count != null && (
        <div className="mt-1 text-2xs text-ink-muted tabular-nums">
          {count} cible{count !== 1 ? 's' : ''}
        </div>
      )}
      {!active && <div className="mt-1 text-2xs text-ink-dim">Non activé</div>}
    </div>
  );
}
